/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t } from '@lingui/macro'
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation'
import PersonIcon from '@mui/icons-material/Person'
import { Typography } from '@mui/material'
import { PatientProviderDTO, usePatientProviders } from '@om1/aspen-api'
import { useProvider } from '@om1/aspen-api/codegen/hooks/useProvider'
import { formatDate } from '@om1/platform-components'
import { FileText, User } from 'lucide-react'
import React from 'react'
import { useParams } from 'react-router-dom'
import { GenericDetailTable } from '../components/GenericDetailTable'
import LoadingComponent from '../components/LoadingComponent'
import GenericDetailPage from './GenericDetailPage'

// Component to display provider information in a clean, consistent layout
const ProviderInfoSection = ({
    title,
    icon,
    children,
    className = '',
    actionButton
}: {
    title: string
    icon: React.ReactNode
    children: React.ReactNode
    className?: string
    actionButton?: React.ReactNode
}) => {
    return (
        <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden mb-4 shadow-sm ${className}`}>
            <div className='flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50'>
                <div className='text-primary-main'>{icon}</div>
                <h3 className='text-base font-semibold text-gray-800 m-0'>{title}</h3>
            </div>
            <div className='p-4'>
                {actionButton}
                {children}
            </div>
        </div>
    )
}

// Info field component for consistent styling
const InfoField = ({ label, value }: { label: string; value?: string | React.ReactNode }) => {
    return (
        <div className='mb-3 last:mb-0'>
            <p className='text-xs text-gray-500 mb-0.5'>{label}</p>
            <p className='text-sm font-medium'>{value || 'N/A'}</p>
        </div>
    )
}

const ProviderDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const { provider } = useProvider(id)
    const { patientProviders } = usePatientProviders({ providerId: id })

    if (provider) {
        return (
            <GenericDetailPage
                title={
                    <div className='flex-1 flex items-center'>
                        <div className='flex items-center mr-4'>
                            <div className='bg-primary-main text-white p-1.5 pt-0.5 rounded-full flex-shrink-0 mr-3'>
                                <MedicalInformationIcon style={{ fontSize: 20 }} />
                            </div>
                            <div className='min-w-0'>
                                <Typography variant='h6' className='font-semibold'>
                                    {provider.name}
                                </Typography>
                            </div>
                        </div>
                        <div className='flex items-center ml-auto'>
                            <div className='bg-blue-100 rounded-md px-4 py-1 flex items-center gap-8'>
                                <div>
                                    <span className='font-medium'>Zip Code:</span> <span>{provider.zipCode || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                }
                backButtonText='To Providers'
                data={provider}
                backRoute='/providers'
                columns={[
                    { key: 'name', type: 'text' },
                    { key: 'createdDttm', type: 'date' },
                    { key: 'updatedDttm', type: 'date' },
                    { key: 'createdByEmail', type: 'text' },
                    { key: 'createdById', type: 'text' },
                    { key: 'updatedById', type: 'text' },
                    { key: 'id', type: 'text' },
                    { key: 'zipCode', type: 'text' }
                ]}
                additionalComponents={[
                    <div key='content-layout' className='grid grid-cols-1 md:grid-cols-12 gap-4 pb-16'>
                        <div className='md:col-span-8 space-y-4'>
                            <ProviderInfoSection title='Provider Details' icon={<User size={18} />}>
                                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                    <InfoField label={t`Name`} value={provider.name} />
                                    <InfoField label={t`Zip Code`} value={provider.zipCode} />
                                    <InfoField label={t`Created By`} value={provider.createdByEmail} />
                                    <InfoField label={t`Created At`} value={formatDate(provider.createdDttm)} />
                                    <InfoField label={t`Updated At`} value={formatDate(provider.updatedDttm)} />
                                </div>
                            </ProviderInfoSection>
                        </div>

                        <div className='md:col-span-12'>
                            <ProviderInfoSection title='Patients' icon={<FileText size={18} />}>
                                <GenericDetailTable
                                    data={patientProviders}
                                    columns={['patient.firstName', 'patient.lastName', 'patient.mobilePhone', 'patient.siteName', 'createdDttm']}
                                    type='patient'
                                    iconTitle={t`Greenlight ID`}
                                    iconResolver={(patientProvider: PatientProviderDTO) => (
                                        <span className='flex items-center'>
                                            {patientProvider.patient.greenlightId ? (
                                                <MedicalInformationIcon className='text-green-800' />
                                            ) : (
                                                <PersonIcon className='text-gray-400' />
                                            )}
                                        </span>
                                    )}
                                    className='max-h-[300px] overflow-y-auto'
                                    stickyHeader={true}
                                />
                            </ProviderInfoSection>
                        </div>
                    </div>
                ]}
            />
        )
    } else {
        return (
            <div className='fixed inset-0 flex flex-col justify-center items-center'>
                <LoadingComponent />
            </div>
        )
    }
}

export default ProviderDetailPage
